import { getP } from 'redux-polyglot'
import cartDeviceThumb from '../img/cart-device-thumb.jpg'
import api from '../libs/api'
import { showToast } from './app'

/**
 * Actions for retrieving user subscription details
 */
export const SUBSCRIPTION_REQUEST = 'ACCOUNT/SUBSCRIPTION/REQUEST'
export const SUBSCRIPTION_SUCCESS = 'ACCOUNT/SUBSCRIPTION/SUCCESS'
export const SUBSCRIPTION_FAILURE = 'ACCOUNT/SUBSCRIPTION/FAILURE'

const subscriptionRequest = () => ({
  type: SUBSCRIPTION_REQUEST,
})

const subscriptionSuccess = subscription => ({
  type: SUBSCRIPTION_SUCCESS,
  payload: subscription,
})

const subscriptionFailure = error => ({
  type: SUBSCRIPTION_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

const processSubscriptions = (subscriptionData, oneTimePurchases) => {
  const subscription = {
    status: '',
    nextPayment: -1,
    price: '',
    merchant: '',
    items: [],
  }

  if (subscriptionData && typeof subscriptionData.items !== 'undefined') {
    subscription.status = subscriptionData.status
    subscription.nextPayment = parseInt(subscriptionData.next_payment, 10)
    subscription.price = subscriptionData.price_formatted
    subscription.merchant = subscriptionData.merchant_slug

    subscriptionData.items
      .filter(item => item.type === 'channel_group')
      .forEach(item =>
        subscription.items.push({
          id: item.item_id,
          name: item.name,
          thumb: item.thumbnail.replace('98x54', '320x180'),
          price: item.price_formatted,
          recurring: item.is_recurring === 'yes',
          recurDetails: {
            length: parseInt(item.period_length, 10),
            type: item.period_type,
          },
          type: 'channel_group',
          status: item.subscription_status,
        })
      )
    subscriptionData.items
      .filter(item => item.type !== 'channel_group')
      .forEach(item =>
        subscription.items.push({
          id: item.item_id,
          name: item.name,
          thumb:
            item.type === 'addon_device'
              ? cartDeviceThumb
              : item.thumbnail.replace('98x54', '320x180'),
          price: item.price_formatted,
          recurring: item.is_recurring === 'yes',
          recurDetails: {
            length: parseInt(item.period_length, 10),
            type: item.period_type,
          },
          type: item.type,
          status: item.subscription_status,
        })
      )
  }

  if (oneTimePurchases instanceof Array && oneTimePurchases.length > 0) {
    oneTimePurchases
      .filter(
        item =>
          item.type === 'channel_group' ||
          item.type === 'premium' ||
          item.type === 'addon_device'
      )
      .forEach(item =>
        subscription.items.push({
          id: item.item_id,
          name: item.payment_name,
          thumb: item.type === 'addon_device' ? cartDeviceThumb : item.thumbnail,
          price: item.payment_amount,
          recurring: false,
          type: item.type,
          status: item.payment_id === 'free' ? 'Free' : 'One-Time',
          until: item.payment_next_date,
        })
      )
  }

  return subscription
}

export const getSubscription = () => (dispatch, getState) => {
  dispatch(subscriptionRequest())
  return api
    .getSubscriptionDetails()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return api
            .getOneTimePurchasesDetails()
            .then(res => res.json())
            .then(
              response2 => {
                if (response2.status === 200) {
                  return dispatch(
                    subscriptionSuccess(
                      processSubscriptions(response.data, response2.data)
                    )
                  )
                } else {
                  return dispatch(
                    subscriptionSuccess(processSubscriptions(response.data, []))
                  )
                }
              },
              () =>
                dispatch(
                  subscriptionFailure(
                    getP(getState()).t('tv_account.error_loading_subscription_details', {
                      _: 'An error occurred while loading subscription details',
                    })
                  )
                )
            )
        } else {
          return dispatch(subscriptionFailure(response.reason))
        }
      },
      () =>
        dispatch(
          subscriptionFailure(
            getP(getState()).t('tv_account.error_loading_subscription_details', {
              _: 'An error occurred while loading subscription details',
            })
          )
        )
    )
}

/**
 * Actions for canceling a subscription item
 */
export const CANCEL_SUBSCRIPTION_ITEM_REQUEST = 'CANCEL/SUBSCRIPTION/ITEM/REQUEST'
export const CANCEL_SUBSCRIPTION_ITEM_SUCCESS = 'CANCEL/SUBSCRIPTION/ITEM/SUCCESS'
export const CANCEL_SUBSCRIPTION_ITEM_FAILURE = 'CANCEL/SUBSCRIPTION/ITEM/FAILURE'

const cancelSubscriptionItemRequest = (type, id) => ({
  type: CANCEL_SUBSCRIPTION_ITEM_REQUEST,
  payload: {
    type,
    id,
  },
})

const cancelSubscriptionItemSuccess = (type, id) => ({
  type: CANCEL_SUBSCRIPTION_ITEM_SUCCESS,
  payload: {
    type,
    id,
  },
})

const cancelSubscriptionItemFailure = (type, id, error) => ({
  type: CANCEL_SUBSCRIPTION_ITEM_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
  meta: {
    type,
    id,
  },
})

export const cancelSubscriptionItem = (type, id) => (dispatch, getState) => {
  dispatch(cancelSubscriptionItemRequest(type, id))
  return api
    .cancelSubscriptionItem(type, id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return Promise.all([
            dispatch(cancelSubscriptionItemSuccess(type, id)),
            dispatch(
              showToast(
                getP(getState()).t('tv_account.subscription_item_canceled', {
                  _: 'Your subscription item has been successfully canceled',
                })
              )
            ),
          ])
        } else {
          return Promise.all([
            dispatch(cancelSubscriptionItemFailure(type, id, response.reason)),
            dispatch(
              showToast(
                getP(getState()).t('tv_account.error_cancel_subscription_item', {
                  _: 'An error occurred while trying to cancel your subscription item',
                }),
                'error'
              )
            ),
          ])
        }
      },
      () =>
        Promise.all([
          dispatch(
            cancelSubscriptionItemFailure(
              type,
              id,
              getP(getState()).t('tv_account.error_cancel_subscription_item', {
                _: 'An error occurred while trying to cancel your subscription item',
              })
            )
          ),
          dispatch(
            showToast(
              getP(getState()).t('tv_account.error_cancel_subscription_item', {
                _: 'An error occurred while trying to cancel your subscription item',
              }),
              'error'
            )
          ),
        ])
    )
}

/**
 * Actions for canceling the subscription
 */
export const CANCEL_SUBSCRIPTION_REQUEST = 'CANCEL/SUBSCRIPTION/REQUEST'
export const CANCEL_SUBSCRIPTION_SUCCESS = 'CANCEL/SUBSCRIPTION/SUCCESS'
export const CANCEL_SUBSCRIPTION_FAILURE = 'CANCEL/SUBSCRIPTION/FAILURE'

const cancelSubscriptionRequest = () => ({
  type: CANCEL_SUBSCRIPTION_REQUEST,
})

const cancelSubscriptionSuccess = () => ({
  type: CANCEL_SUBSCRIPTION_SUCCESS,
})

const cancelSubscriptionFailure = error => ({
  type: CANCEL_SUBSCRIPTION_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const cancelSubscription = () => (dispatch, getState) => {
  dispatch(cancelSubscriptionRequest())
  return api
    .cancelSubscription()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return Promise.all([
            dispatch(cancelSubscriptionSuccess()),
            dispatch(
              showToast(
                getP(getState()).t('tv_account.subscription_canceled', {
                  _: 'Your subscription has been successfully canceled',
                })
              )
            ),
          ])
        } else {
          return Promise.all([
            dispatch(cancelSubscriptionFailure(response.reason)),
            dispatch(
              showToast(
                getP(getState()).t('tv_account.error_cancel_subscription', {
                  _: 'An error occurred while trying to cancel your subscription',
                }),
                'error'
              )
            ),
          ])
        }
      },
      () =>
        Promise.all([
          dispatch(
            cancelSubscriptionFailure(
              getP(getState()).t('tv_account.error_cancel_subscription', {
                _: 'An error occurred while trying to cancel your subscription',
              })
            )
          ),
          dispatch(
            showToast(
              getP(getState()).t('tv_account.error_cancel_subscription', {
                _: 'An error occurred while trying to cancel your subscription',
              }),
              'error'
            )
          ),
        ])
    )
}
